import { useQuery } from "@tanstack/react-query";
import { apiRequest, apiDownload } from "../hooks/apiRequest";
import { triggerBrowserDownload } from "../utils/download";
import { reportKeys } from "../utils/queryKeys";
import { Severity, Priority, TaskStatus, ProjectStatus, SprintStatus } from "../types/interfaces";

export interface UserBreakdown {
  userId: string;
  name: string;
  totalMinutes: number;
  billableMinutes?: number;
  cost: number;
}

export interface ProjectBreakdown {
  projectId: string;
  name: string;
  totalMinutes: number;
}

export interface UserProjectBreakdown {
  projectId: string;
  projectName: string | null;
  totalMinutes: number;
}

export interface ByProjectReport {
  totalMinutes: number;
  billableMinutes: number;
  totalCost: number;
  byUser: UserBreakdown[];
}

export interface ByUserReport {
  totalMinutes: number;
  totalCost: number;
  byProject: UserProjectBreakdown[];
}

export interface TimelineBucket {
  bucket: string;
  totalMinutes: number;
}

export function useReportByProject(projectId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: reportKeys.byProject(projectId || "", from, to),
    queryFn: async () =>
      (await apiRequest<ByProjectReport>(`reports/by-project?projectId=${projectId}&from=${from}&to=${to}`)).data,
    enabled: !!projectId,
  });
}

export function useReportByUser(userId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: reportKeys.byUser(userId || "", from, to),
    queryFn: async () =>
      (await apiRequest<ByUserReport>(`reports/by-user?userId=${userId}&from=${from}&to=${to}`)).data,
    enabled: !!userId,
  });
}

export function useReportTimeline(
  scope: "project" | "all" | undefined,
  scopeId: string | undefined,
  from: string,
  to: string,
  granularity: "day" | "week" | "month"
) {
  return useQuery({
    queryKey: reportKeys.timeline(scope || "", scopeId || "", from, to, granularity),
    queryFn: async () =>
      (
        await apiRequest<TimelineBucket[]>(
          `reports/timeline?scope=${scope}${scopeId ? `&scopeId=${scopeId}` : ""}&from=${from}&to=${to}&granularity=${granularity}`
        )
      ).data,
    enabled: scope === "all" || (!!scope && !!scopeId),
  });
}

export interface VelocityPoint {
  sprintId: string;
  sprintName: string;
  completedPoints: number;
}

export function useReportVelocity(projectId?: string) {
  return useQuery({
    queryKey: reportKeys.velocity(projectId || ""),
    queryFn: async () => (await apiRequest<VelocityPoint[]>(`reports/velocity${projectId ? `?projectId=${projectId}` : ""}`)).data,
  });
}

/* ------------------------------------------------------- manager dashboard */

export type SeverityCounts = Record<Severity, number>;
export type PriorityCounts = Record<Priority, number>;
export type StatusCounts = Record<TaskStatus, number>;

export interface OverviewReport {
  projectsByStatus: Record<ProjectStatus, number>;
  tickets: { open: number; unanswered: number; bySeverity: SeverityCounts; byPriority: PriorityCounts };
  tasks: { open: number; overdue: number; byStatus: StatusCounts; bySeverity: SeverityCounts; byPriority: PriorityCounts };
}

export interface AssigneeRow {
  userId: string;
  name: string;
  role: "manager" | "agent";
  projects: { projectId: string; name: string; status: ProjectStatus }[];
  tasksByStatus: StatusCounts;
  openTasks: number;
  openTickets: number;
  loggedMinutes: number;
}

export interface TasksByProjectRow {
  projectId: string;
  name: string;
  status: ProjectStatus;
  severity: Severity;
  priority: Priority;
  total: number;
  byStatus: StatusCounts;
  bySeverity: SeverityCounts;
  byPriority: PriorityCounts;
  backlog: number;
  inActiveSprint: number;
}

export interface SlaTarget {
  firstResponseMinutes: number;
  resolutionMinutes: number;
}
export type SlaTargets = Record<Severity, SlaTarget>;

export interface ResponseGroup {
  key: string;
  name: string;
  count: number;
  solved: number;
  avgFirstResponseMinutes: number | null;
  medianFirstResponseMinutes: number | null;
  avgResolutionMinutes: number | null;
  medianResolutionMinutes: number | null;
  firstResponseBreaches: number;
  resolutionBreaches: number;
}

export interface AtRiskTicket {
  ticketId: string;
  subject: string;
  projectId: string;
  projectName: string | null;
  assigneeName: string | null;
  severity: Severity;
  priority: Priority;
  phase: "first_response" | "resolution";
  ageMinutes: number;
  limitMinutes: number;
  breached: boolean;
  consumed: number;
}

export interface TicketResponseReport {
  range: { from: string; to: string };
  targets: SlaTargets;
  summary: ResponseGroup;
  unanswered: number;
  bySeverity: ResponseGroup[];
  byProject: ResponseGroup[];
  byAssignee: ResponseGroup[];
  atRisk: AtRiskTicket[];
}

export interface GanttReport {
  projects: {
    projectId: string;
    name: string;
    status: ProjectStatus;
    severity: Severity;
    priority: Priority;
    startDate: string | null;
    targetDate: string | null;
  }[];
  sprints: {
    sprintId: string;
    name: string;
    status: SprintStatus;
    startDate: string;
    endDate: string;
    taskCount: number;
    projectIds: string[];
  }[];
}

export interface ActivityBucket {
  bucket: string;
  ticketsOpened: number;
  ticketsSolved: number;
  totalMinutes: number;
}

export function useReportOverview(enabled = true) {
  return useQuery({
    queryKey: reportKeys.overview(),
    queryFn: async () => (await apiRequest<OverviewReport>("reports/overview")).data,
    enabled,
  });
}

export function useReportByAssignee(from = "", to = "", enabled = true) {
  const qs = [from && `from=${from}`, to && `to=${to}`].filter(Boolean).join("&");
  return useQuery({
    queryKey: reportKeys.byAssignee(from, to),
    queryFn: async () => (await apiRequest<AssigneeRow[]>(`reports/by-assignee${qs ? `?${qs}` : ""}`)).data,
    enabled,
  });
}

export function useReportTasksByProject(enabled = true) {
  return useQuery({
    queryKey: reportKeys.tasksByProject(),
    queryFn: async () => (await apiRequest<TasksByProjectRow[]>("reports/tasks-by-project")).data,
    enabled,
  });
}

export function useReportTicketResponse(from = "", to = "", enabled = true) {
  const qs = [from && `from=${from}`, to && `to=${to}`].filter(Boolean).join("&");
  return useQuery({
    queryKey: reportKeys.ticketResponse(from, to),
    queryFn: async () => (await apiRequest<TicketResponseReport>(`reports/ticket-response${qs ? `?${qs}` : ""}`)).data,
    enabled,
  });
}

export function useReportGantt(enabled = true) {
  return useQuery({
    queryKey: reportKeys.gantt(),
    queryFn: async () => (await apiRequest<GanttReport>("reports/gantt")).data,
    enabled,
  });
}

export function useReportActivity(from: string, to: string, granularity: "day" | "week" | "month" = "week", enabled = true) {
  return useQuery({
    queryKey: reportKeys.activity(from, to, granularity),
    queryFn: async () =>
      (await apiRequest<ActivityBucket[]>(`reports/activity?from=${from}&to=${to}&granularity=${granularity}`)).data,
    enabled,
  });
}

/** Downloads the support-team report (no cost data) as an Excel workbook or a PDF. */
export async function downloadSupportReport(format: "xlsx" | "pdf", from: string, to: string) {
  const { blob, filename } = await apiDownload(`reports/export?format=${format}&from=${from}&to=${to}`);
  triggerBrowserDownload(blob, filename);
}
